import { parse } from 'url';
import React, { useEffect, useRef, useContext, useState } from 'react';
import PropTypes from 'prop-types';
import { useRouter } from 'next/router';
import { useSearchParams } from 'next/navigation';
import { useSession as useAuthSession } from 'next-auth/react';
import { interpolate } from '@format/interpolate';
import { Redirect } from '@web/router';
import { LoadingView } from '@web/components';
import { EmptyHeader } from '@web/molecules';
import { useSyncPathToMachine } from '../useSyncPathToMachine';
import { onboardingInitiated, onboardingBackButtonClicked } from '../events';
import { translateContentToProps } from '../translation';
import { PrefixedReporterProvider, useReporter } from '../../reporter';
import { PlanLoader } from '../PlanLoader';
import { useShopifyOfferContext } from '../../offer/shopifyContext';
import {
  CHECKOUT_SUBPATH,
  DELETE_PET_SUBPATH,
  MEALPREP_SELECTION_SUBPATH,
  RECIPES_SELECTION_SUBPATH,
  RECOMMENDED_PLAN_SUBPATH,
} from '../routing';
import { OnboardingProvider, OnboardingContext } from './context';
import { getActionFromKey, getKeyFromPath } from './functions';
import { components } from './components';
import { ROOT_SUBPATH, pathToAction, STATE, unmappedStates } from './constants';
import { InterstitialContainer, LoadingInterstitial } from './styles.js';

const OnboardingFlow = ({ content }) => {
  const {
    pet_id,
    pet,
    pets,
    customer,
    cart,
    send,
    stateKey,
    petIsLoading,
    isPlanTypesLoading,
    isCombinationsLoading,
    service,
    editPetFlow,
  } = useContext(OnboardingContext);
  const shouldSkipPlanLoader = Boolean(editPetFlow?.current);
  const reporter = useReporter();
  const { formattedAmount } = useShopifyOfferContext();

  const [showPlanSelection, setShowPlanSelection] =
    useState(shouldSkipPlanLoader);

  const router = useRouter();
  const searchParams = useSearchParams();
  const [subpath, slug] = router.query.path ?? [];
  const pathname = slug ? `${subpath}/${slug}` : subpath;
  const lastComponentRef = useRef({ component: LoadingView, props: {} });
  const routeChanging = useRef(false);
  const skipInterstitialRef = useRef(false);
  if (shouldSkipPlanLoader) {
    skipInterstitialRef.current = true;
  }

  let props = translateContentToProps(content);
  // Only fire analytics tag once on first render
  useEffect(() => {
    reporter.tag(onboardingInitiated());
  }, []); //eslint-disable-line react-hooks/exhaustive-deps

  // useEffect(() => {
  //   // this is forcing LoadingView to render and grant plansApi.isValidating
  //   // time to process, which fixes the double-triple+ rendering/analytics tag
  //   // firing for the default render of this component
  //   // (which contains FreshRecipesSelectionForm)
  //   // please note that this fix is ONLY for when going backwards
  //   // (Back in browser or clicking Edit) from CheckoutContainer

  //   if (skipInterstitialRef.current && !showPlanSelection) {
  //     setTimeout(() => {
  //       setShowPlanSelection(true);
  //     }, 100);
  //   }
  // }, [showPlanSelection]);

  useSyncPathToMachine(
    routeChanging,
    false,
    stateKey,
    unmappedStates,
    pet_id,
    setShowPlanSelection,
  );

  // Router event handlers
  useEffect(() => {
    // When the URL changes via a router initiated change, change the state machine step to match the new URL
    // TODO - Revisit this functionality post launch given Add Pet and Edit Pet no longer live in the state machine
    const syncMachineToPath = (url, { browserInitiated }) => {
      routeChanging.current = true;
      const parsedUrl = parse(url);

      const key = getKeyFromPath(parsedUrl.pathname, ROOT_SUBPATH);

      if (stateKey !== key) {
        let action = pathToAction[key];

        // Handling browser back button
        if (browserInitiated) action = getActionFromKey(key, service);

        // Add pup link click
        if (key === STATE.pupInfo || key === STATE.about) {
          // If we're currently on checkout and going to pupInfo,
          // we're in the new pet flow.
          const isNewPet = router.asPath.includes(CHECKOUT_SUBPATH);
          action &&
            send(
              action({
                pets,
                pet_id: isNewPet ? 'new_pet' : '',
                pet: {},
                cart,
                customer,
                personalizedPlan: false,
              }),
            );
        } else {
          // the browser url does not have the updated url with the target pet_id when syncMachineToPath is called. grab from the target url.
          const searchParams = new URLSearchParams(parsedUrl.query);
          const petId = searchParams.get('pet_id') ?? pet_id;
          action &&
            send(
              action({
                pets,
                pet,
                pet_id: petId,
                cart,
                customer,
              }),
            );
        }
      }
    };

    const trackPreviousUrl = nextKey => {
      const currentPath = router.asPath;
      // If we're going from Checkout -> Plan Selection
      // we should skip the interstitial. Using a ref
      // ensures we don't trigger unnecessary renders.
      skipInterstitialRef.current =
        (currentPath.includes(CHECKOUT_SUBPATH) ||
          currentPath.includes(DELETE_PET_SUBPATH) ||
          currentPath.includes(RECIPES_SELECTION_SUBPATH) ||
          currentPath.includes(RECOMMENDED_PLAN_SUBPATH)) &&
        (nextKey.includes(MEALPREP_SELECTION_SUBPATH) ||
          nextKey.includes(RECIPES_SELECTION_SUBPATH));
    };

    router.events.on('routeChangeStart', syncMachineToPath);
    router.events.on('beforeHistoryChange', trackPreviousUrl);
    // When user hits the browser "back" button, change the state machine step to match the new URL
    router.beforePopState(({ as }) => {
      syncMachineToPath(as, { browserInitiated: true });
      reporter.tag(onboardingBackButtonClicked(stateKey));
      return true;
    });

    return () => {
      router.events.off('routeChangeStart', syncMachineToPath);
      router.events.off('beforeHistoryChange', trackPreviousUrl);
      router.beforePopState(() => true);
    };
  }, [
    cart,
    customer,
    pet,
    pet_id,
    pets,
    reporter,
    router,
    searchParams,
    send,
    service,
    stateKey,
  ]);

  // props.component only exists on initial render
  if (props.component === 'redirect_route' || stateKey === STATE.initial) {
    if (pathname === DELETE_PET_SUBPATH) {
      return <LoadingView variant="deletePet" petName={pet?.name} />;
    }
    return <LoadingView />;
  }

  let Component = components[stateKey];

  if ((pathname !== stateKey || !Component) && stateKey !== STATE.initial) {
    Component = lastComponentRef.current.component;
    props = lastComponentRef.current.props;
  } else lastComponentRef.current = { component: Component, props };

  // TODO: // ES - 1508
  if (
    (stateKey === STATE.planSelection || stateKey === STATE.recommendedPlan) &&
    pathname === stateKey
  ) {
    if (
      (isPlanTypesLoading || petIsLoading || isCombinationsLoading) &&
      skipInterstitialRef.current
    ) {
      return <LoadingView />;
    } else if (!showPlanSelection && !skipInterstitialRef.current) {
      return (
        <PrefixedReporterProvider prefix="Onboarding">
          <InterstitialContainer>
            <EmptyHeader />
            <LoadingInterstitial>
              <PlanLoader
                header={props.planLoaderHeader}
                messages={interpolate(props.planLoaderMessages, {
                  discount: formattedAmount,
                  pupName: pet?.name || 'your pup',
                })}
                onClick={() => {
                  setShowPlanSelection(true);
                }}
                buttonText={interpolate(props.planLoaderButtonText, {
                  discount: formattedAmount,
                })}
                isLoading={isCombinationsLoading}
              />
            </LoadingInterstitial>
          </InterstitialContainer>
        </PrefixedReporterProvider>
      );
    }
  }
  return <Component {...props} />;
};

OnboardingFlow.displayName = 'OnboardingFlow';

OnboardingFlow.propTypes = {
  content: PropTypes.object.isRequired,
  component: PropTypes.string,
  noRecipesInfo: PropTypes.object,
  planLoaderMessages: PropTypes.arrayOf(PropTypes.object),
  planLoaderButtonText: PropTypes.string,
  body: PropTypes.arrayOf(
    PropTypes.shape({
      _uid: PropTypes.string,
    }),
  ),
};

// ************************

export const OnboardingFlowContainer = ({ content }) => {
  const { data: session, status } = useAuthSession();
  const isShopifyAuthenticated = session && status === 'authenticated';

  if (isShopifyAuthenticated) {
    return <Redirect to="/account/" />;
  }

  return (
    <OnboardingProvider>
      <OnboardingFlow content={content} />
    </OnboardingProvider>
  );
};

OnboardingFlowContainer.displayName = 'OnboardingFlowContainer';

OnboardingFlowContainer.propTypes = {
  content: PropTypes.object.isRequired,
};
